import { Directive, signal } from '@angular/core';
import { Model } from '@df/catalog/model';
import { IProductDataV2 } from '@df/catalog/product/product';
import { ProductDecorator } from '../../../../model/product/product.decorator';
import { ModelCarouselComponentDef } from '../model-carousel/model-carousel.component.def';
import { EModelCarouselVariant, IModelCarouselDataItem } from '../model-carousel/model-carousel.interface';
import { EModelProductLabel, EModelProductView, ICustomCategoryItem, ILocalModelCarouselData } from './local-model-carousel.interface';

let uid = 0;

@Directive()
export abstract class LocalModelCarouselComponent<
    T extends Model,
    D extends ILocalModelCarouselData = ILocalModelCarouselData
> extends ModelCarouselComponentDef<T, D> {
    readonly uid = uid++;

    protected readonly canShow = signal(true)

    public get EModelProductView(): typeof EModelProductView {
        return EModelProductView;
    }

    public get EModelProductLabel(): typeof EModelProductLabel {
        return EModelProductLabel;
    }

    override get canLoop(): boolean {
        return super.canLoop && !!this.component?.carousel_loop;
    }

    get showCarousel(): boolean {
        return !this.component?.product_list;
    }

    get limit(): number {
        return this.component?.limit || 36;
    }

    get slidesPerView() {
        return [2.5, 4.2, 4.2, 4.2][this.client.sizeId];
    }

    get slidesPerViewRound() {
        return Math.round(this.slidesPerView);
    }

    get spaceBetween() {
        return [2, 4, 4, 4][this.client.sizeId];
    }

    items:
        | {
            id: string | number;
            model: T;
            modelView?: boolean;
            altView?: boolean;
        }[]
        | ICustomCategoryItem[] = [];

    hasModelView(model?: IModelCarouselDataItem): boolean {
        if (model?.product_view) {
            return model?.product_view === EModelProductView.Model;
        } else if (this.component?.product_view) {
            return this.component?.product_view === EModelProductView.Model;
        }

        return false;
    }

    hasAltView(model?: IModelCarouselDataItem): boolean {
        if (model?.product_label) {
            return model?.product_label === EModelProductLabel.Alternative;
        } else if (this.component?.product_label) {
            return this.component?.product_label === EModelProductLabel.Alternative;
        }

        return false;
    }

    override get cmsObservedData(): string[] {
        return [...super.cmsObservedData, 'items', 'product_*', 'carousel_*'];
    }

    protected override onModelsSet(): void {
        super.onModelsSet();

        if (this.component?.custom_items && this.component?.variant === EModelCarouselVariant.CustomElements) {
            this.items = this.component.custom_items;
            this.type = 'custom';
        } else {
            this.items = this.models
                .filter(model => !(model.data as ProductDecorator<IProductDataV2>)?.isBanner)
                .map(model => {
                    const item = this.component?.items?.find(item => item.id === model.id);

                    return {
                        id: model.id,
                        model,
                        modelView: this.hasModelView(item),
                        altView: this.hasAltView(item)
                    };
                });
        }

        this.detectChanges();
    }
}
